import { useEffect, useMemo, useState } from "react";
import Card from "@mui/material/Card";
import {
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import CallMadeIcon from "@mui/icons-material/CallMade";
import { useNavigate } from "react-router-dom";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

import { getStatusMappings, deleteStatusMapping } from "api/apiService";
import { notifyCenter } from "utils/toast";
import ConfirmDeleteModal from "layouts/statusSettings/ConfirmDeleteModal";

// Key by slug → label so the table can show Norwegian label in addition to slug.
function wcLabelMap(statuses) {
  const map = {};
  for (const s of statuses || []) map[s.slug] = s.label;
  return map;
}

const SELECT_SX = {
  height: 40,
  "&.MuiInputBase-root": {
    display: "flex !important",
    placeItems: "initial !important",
    padding: "0 !important",
  },
  "& .MuiSelect-select": {
    display: "flex !important",
    alignItems: "center !important",
    minHeight: "40px !important",
    boxSizing: "border-box",
    padding: "0 32px 0 14px !important",
    flex: 1,
    width: "100% !important",
  },
};

const DIRECTION_META = {
  both:        { label: "Begge",            color: "info",      icon: SwapHorizIcon },
  wc_to_bmax:  { label: "WC \u2192 Bmax",   color: "secondary", icon: CallReceivedIcon },
  bmax_to_wc:  { label: "Bmax \u2192 WC",   color: "secondary", icon: CallMadeIcon },
};

function StatusMappings() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [wcStatuses, setWcStatuses] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [directionFilter, setDirectionFilter] = useState("");
  const pageSize = 50;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [deleteId, setDeleteId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function fetchData() {
    setLoading(true);
    setError(null);
    try {
      const res = await getStatusMappings({
        limit: pageSize,
        page_number: page,
        direction: directionFilter || undefined,
      });
      if (res?.ok) {
        setRows(res.mappings || []);
        setWcStatuses(Array.isArray(res.wc_statuses) ? res.wc_statuses : []);
        setTotalRecords(res.total ?? 0);
        setTotalPages(res.total_pages ?? 1);
      } else {
        setError(res?.error || "Kunne ikke hente mappinger");
        setRows([]);
      }
    } catch (e) {
      setError(e?.message || "Nettverksfeil");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, directionFilter]);

  const offset = useMemo(() => (page - 1) * pageSize, [page]);

  function openEdit(id) {
    navigate(`/status-mappings/edit/${id}`);
  }

  function handleDeleteClick(id) {
    setDeleteId(id);
    setConfirmOpen(true);
  }

  async function confirmDelete() {
    setConfirmOpen(false);
    if (!deleteId) return;
    setLoading(true);
    try {
      const res = await deleteStatusMapping(deleteId);
      if (res?.ok) {
        notifyCenter("success", res.message || "Slettet", { duration: 2000 });
        setDeleteId(null);
        fetchData();
      } else {
        notifyCenter("error", res?.message || res?.error || "Kunne ikke slette", { duration: 3000 });
      }
    } catch (err) {
      notifyCenter("error", "Noe gikk galt ved sletting", { duration: 3000 });
      console.error("Delete mapping error:", err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <DashboardLayout>
      <ConfirmDeleteModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
        title="Slette mapping?"
        message="Vil du slette denne status-mappingen? Status-synkronisering mellom WC og bmax vil ikke lenger fungere for denne kombinasjonen."
      />

      <SoftBox py={3} px={{ xs: 1, md: 2 }}>
        <Card sx={{ position: "relative" }}>
          {/* Header */}
          <SoftBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
            p={3}
          >
            <SoftBox>
              <SoftTypography variant="h5" fontWeight="bold">
                Status-mapping
              </SoftTypography>
              <SoftTypography variant="button" color="text">
                Hvordan WC-status oversettes til bmax-status og omvendt
              </SoftTypography>
            </SoftBox>
            <SoftButton
              variant="gradient"
              color="info"
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              onClick={() => navigate("/status-mappings/create")}
            >
              Ny mapping
            </SoftButton>
          </SoftBox>
          <Divider sx={{ my: 0 }} />

          {/* Info callout */}
          <SoftBox px={3} pt={3}>
            <SoftBox
              sx={(t) => ({
                display: "flex",
                gap: 1.5,
                p: 2,
                borderRadius: 1.5,
                backgroundColor: t.palette.info.light + "15",
                borderLeft: `4px solid ${t.palette.info.main}`,
              })}
            >
              <SoftTypography variant="caption" color="text" component="div" sx={{ lineHeight: 1.6 }}>
                <strong>WC → Bmax (Innkommende)</strong>: Når WooCommerce rapporterer en status-endring via webhook, blir tilsvarende bmax-status oppdatert hvis <em>Auto-transisjon</em> er på.
                <br />
                <strong>Bmax → WC (Utgående)</strong>: Når admin endrer status i bmax, pushes tilsvarende WC-status til WooCommerce.
                <br />
                <strong>Guard pre-payment</strong>: Auto-transisjon kjører kun hvis ordren er i en pre-payment tilstand (f.eks. &laquo;Kvalitetssikres&raquo;). Brukes typisk for <code>processing</code> for å ikke overstyre admins manuelle valg.
              </SoftTypography>
            </SoftBox>
          </SoftBox>

          {/* Filters */}
          <SoftBox p={3} pb={2}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={5} md={3}>
                <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                  Retning
                </SoftTypography>
                <FormControl fullWidth size="small">
                  <Select
                    value={directionFilter}
                    displayEmpty
                    onChange={(e) => {
                      setDirectionFilter(e.target.value);
                      setPage(1);
                    }}
                    sx={SELECT_SX}
                  >
                    <MenuItem value="">Alle retninger</MenuItem>
                    <MenuItem value="both">Begge</MenuItem>
                    <MenuItem value="wc_to_bmax">WC → Bmax</MenuItem>
                    <MenuItem value="bmax_to_wc">Bmax → WC</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </SoftBox>
          <Divider sx={{ my: 0 }} />

          {error && (
            <SoftBox px={3} py={2}>
              <SoftTypography variant="button" color="error">
                {error}
              </SoftTypography>
            </SoftBox>
          )}

          {/* Table */}
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table
              size="small"
              sx={{
                width: "100%",
                minWidth: 1000,
                tableLayout: "fixed",
                "& thead": {
                  display: "table-header-group",
                  padding: 0,
                  borderRadius: 0,
                },
                "& tbody": { display: "table-row-group" },
                "& tr": { display: "table-row" },
                "& td, & th": {
                  display: "table-cell",
                  padding: "12px 14px",
                  borderBottom: (t) => `1px solid ${t.palette.grey[200]}`,
                  fontSize: 13,
                  verticalAlign: "middle",
                },
                "& thead th": {
                  backgroundColor: (t) => t.palette.grey[100],
                  color: (t) => t.palette.text.secondary,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  fontSize: 11,
                  letterSpacing: 0.4,
                  whiteSpace: "nowrap",
                },
                "& tbody tr": { cursor: "pointer" },
                "& tbody tr:hover": {
                  backgroundColor: (t) => t.palette.grey[50],
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "8%" }}>ID</TableCell>
                  <TableCell sx={{ width: "18%" }}>WC-status</TableCell>
                  <TableCell sx={{ width: "18%" }}>Bmax-status</TableCell>
                  <TableCell sx={{ width: "16%" }}>Retning</TableCell>
                  <TableCell sx={{ width: "15%" }} align="center">Auto-transisjon</TableCell>
                  <TableCell sx={{ width: "15%" }} align="center">Pre-payment guard</TableCell>
                  <TableCell sx={{ width: "10%" }} align="right">Handlinger</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && rows.length === 0 && (
                  <TableRow sx={{ cursor: "default !important" }}>
                    <TableCell colSpan={7} sx={{ py: 6 }}>
                      <SoftBox display="flex" flexDirection="column" alignItems="center" gap={1}>
                        <InboxOutlinedIcon sx={(t) => ({ fontSize: 48, color: t.palette.grey[400] })} />
                        <SoftTypography variant="body2" color="secondary">
                          Ingen mappinger enda – opprett den første
                        </SoftTypography>
                        <SoftButton
                          variant="gradient"
                          color="info"
                          size="small"
                          startIcon={<AddIcon fontSize="small" />}
                          onClick={() => navigate("/status-mappings/create")}
                          sx={{ mt: 1 }}
                        >
                          Ny mapping
                        </SoftButton>
                      </SoftBox>
                    </TableCell>
                  </TableRow>
                )}

                {rows.map((m) => {
                  const dir = DIRECTION_META[m.direction] || { label: m.direction, color: "default", icon: SwapHorizIcon };
                  const DirIcon = dir.icon;
                  const wcLabels = wcLabelMap(wcStatuses);
                  const wcLabel = wcLabels[m.wc_status];
                  return (
                    <TableRow key={m.id} hover onClick={() => openEdit(m.id)}>
                      <TableCell sx={{ color: "text.secondary", fontFamily: "monospace" }}>
                        #{m.id}
                      </TableCell>
                      <TableCell>
                        {wcLabel ? (
                          <>
                            <div>{wcLabel}</div>
                            <code style={{ fontSize: 11, color: "#888" }}>{m.wc_status}</code>
                          </>
                        ) : (
                          <code style={{ fontSize: 12 }}>{m.wc_status}</code>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                        {m.bmax_status}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<DirIcon fontSize="small" />}
                          label={dir.label}
                          color={dir.color}
                          size="small"
                          sx={{ fontWeight: 600, height: 22 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={m.auto_transition ? "Aktivert" : "Deaktivert"}
                          color={m.auto_transition ? "success" : "default"}
                          size="small"
                          variant={m.auto_transition ? "filled" : "outlined"}
                          sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={m.guard_pre_payment ? "På" : "Av"}
                          color={m.guard_pre_payment ? "warning" : "default"}
                          size="small"
                          variant={m.guard_pre_payment ? "filled" : "outlined"}
                          sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ whiteSpace: "nowrap" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Tooltip title="Rediger">
                          <IconButton size="small" onClick={() => openEdit(m.id)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Slett">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(m.id)}
                            sx={{ color: "error.main" }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {rows.length > 0 && (
            <>
              <Divider sx={{ my: 0 }} />
              <SoftBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
                p={3}
              >
                <SoftTypography variant="caption" color="secondary">
                  Viser {rows.length ? offset + 1 : 0}
                  {"–"}
                  {offset + rows.length} av {totalRecords}
                </SoftTypography>
                <SoftBox display="flex" alignItems="center" gap={1}>
                  <SoftButton
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1 || loading}
                  >
                    Forrige
                  </SoftButton>
                  <SoftTypography variant="caption" color="text" px={1}>
                    Side {page} / {totalPages}
                  </SoftTypography>
                  <SoftButton
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= totalPages || loading}
                  >
                    Neste
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </>
          )}

          {loading && (
            <SoftBox
              sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 10,
                borderRadius: "inherit",
              }}
            >
              <CircularProgress color="info" size={36} />
            </SoftBox>
          )}
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}

export default StatusMappings;
